import React from 'react';
import Grid3x3 from 'lucide-react/dist/esm/icons/grid-3x3.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { charityConfig } from '../../config/charity';
import InlineTranslationEdit from '../InlineTranslationEdit';
import { useContent } from '../../contexts/ContentContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';

export default function CenterSquareCharity({ editMode = false, handleTranslationUpdate }) {
  const { c } = useContent();
  const { siteDetails } = useSiteConfig() || {};
  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl;
  const brandName = siteDetails?.siteName || charityConfig.name;
  const gridPattern = [
    [0, 0, 0, 0, 0],
    [0, 1, 1, 1, 0],
    [0, 1, 2, 1, 0],
    [0, 1, 1, 1, 0],
    [0, 0, 0, 0, 0],
  ];

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-green-50 to-white dark:from-emerald-950 dark:via-green-950 dark:to-gray-900 rounded-2xl shadow-lg border border-emerald-200 dark:border-emerald-800">
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-400/20 to-green-400/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-teal-400/20 to-green-400/20 rounded-full blur-2xl"></div>

      <div className="relative p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg shadow-md">
            <Grid3x3 className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white"><InlineTranslationEdit translationKey="CharityCenterSquareTitle" value={c('CharityCenterSquareTitle', 'Center Square')} fallback="Center Square" onUpdate={handleTranslationUpdate} editMode={editMode} /></h3>
        </div>

        <div className="flex justify-center mb-4">
          <div className="relative bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/40 dark:to-green-900/40 rounded-xl p-3 shadow-inner border border-emerald-200 dark:border-emerald-700">
            <div className="grid grid-cols-5 gap-1">
              {gridPattern.flat().map((cell, index) => (
                <div
                  key={index}
                  className={`w-8 h-8 rounded transition-all ${
                    cell === 2
                      ? 'bg-gradient-to-br from-emerald-500 to-green-500 shadow-lg scale-110 flex items-center justify-center'
                      : cell === 1
                      ? 'bg-gradient-to-br from-emerald-300 to-green-300 dark:from-emerald-600 dark:to-green-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  {cell === 2 && (
                    <Heart className="w-4 h-4 text-white fill-current" />
                  )}
                </div>
              ))}
            </div>

            <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 w-20 h-20 rounded-full bg-white dark:bg-gray-800 shadow-xl border-4 border-emerald-400 dark:border-emerald-600 flex items-center justify-center overflow-hidden">
              <img
                src={brandLogo}
                alt={brandName}
                className="w-14 h-14 object-contain"
              />
            </div>
          </div>
        </div>

        <div className="mt-14 text-center mb-4">
          <h4 className="font-bold text-gray-900 dark:text-white text-lg">{brandName}</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">{charityConfig.tagline}</p>
        </div>

        <div className="bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 rounded-xl p-4 border border-emerald-200 dark:border-emerald-700">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Grid3x3 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300"><InlineTranslationEdit translationKey="CharityCenterSquareSupports" value={c('CharityCenterSquareSupports', 'Every Square Supports')} fallback="Every Square Supports" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
          </div>
          <p className="text-sm text-emerald-800 dark:text-emerald-200 text-center leading-relaxed">
            {charityConfig.impactStatement}
          </p>
        </div>

        <div className="mt-4 text-center">
          <a
            href={charityConfig.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors group"
          >
            <InlineTranslationEdit translationKey="CharityCenterSquareCTA" value={c('CharityCenterSquareCTA', 'Be part of the winning team')} fallback="Be part of the winning team" onUpdate={handleTranslationUpdate} editMode={editMode} />
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
}
